import {
  CancelMeetingRequestMutation,
  CancelMeetingRequestMutationVariables,
  MeetingRequestStatus,
  RequestMeetingRescheduleMutation,
  RequestMeetingRescheduleMutationVariables,
  RequestMeetingsMutation,
  RequestMeetingsMutationVariables,
  SectionMeetingsQuery,
  SectionMeetingsQueryVariables,
  SectionMeetings_MeetingFragmentFragment,
  StartEndTime,
} from "@outschool/gql-frontend-generated";
import { dayjs, startOfIsoWeekUtc } from "@outschool/time";
import {
  gql,
  useMutation,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import { dataIdFromObject } from "@outschool/ui-gql";
import React from "react";
import { v4 } from "uuid";

import CancelOneOnOneModal from "../components/meetings/CancelOneOnOneModal";
import SectionManageToolbar from "../components/nav/SectionManageToolbar";

const sectionMeetings_RequestFragment = gql`
  fragment SectionMeetings_RequestFragment on MeetingRequest {
    __typename
    uid
    start_time
    end_time
    status
    billedOn
    is_recurring
  }
`;

const sectionMeetings_MeetingFragment = gql`
  fragment SectionMeetings_MeetingFragment on Meeting {
    __typename
    uid
    start_time
    end_time
    created_at
    deleted_at
    paidAt
    billedOn
    recurring
    recurringSchedule {
      __typename
      uid
      end_at
    }
    rescheduleRequests {
      ...SectionMeetings_RequestFragment
    }
    ...CancelOneOnOneModal_selectedMeeting
  }
  ${sectionMeetings_RequestFragment}
  ${CancelOneOnOneModal.fragments.selectedMeeting}
`;

const sectionMeetingsQuery = gql`
  query SectionMeetings(
    $sectionUid: ID!
    $startAfter: DateTime!
    $startBefore: DateTime!
  ) {
    section(uid: $sectionUid) {
      ...SectionManageToolbar_section
      activity {
        uid
        duration_minutes
        price_cents
        refundPolicy {
          ...CancelOneOnOneModal_refundPolicy
        }
        ...SectionManageToolbar_activity
      }
      enrollments {
        uid
        learner {
          uid
          name
          avatar
          age
        }
        payments {
          paid_at
          esa_order_id
          order_slug_id
          organization {
            uid
          }
        }
        pauseWindow {
          uid
        }
      }
      meetingCount
      meetings(startAfter: $startAfter, startBefore: $startBefore) {
        ...SectionMeetings_MeetingFragment
      }
      latestProgressReportDate
      meetingRequests(
        filters: { startAfter: $startAfter, startBefore: $startBefore }
      ) {
        ...SectionMeetings_RequestFragment
      }
      meetingConflictInfo {
        meetingUid
        conflictingSectionUid
      }
    }
  }
  ${sectionMeetings_MeetingFragment}
  ${SectionManageToolbar.fragments.section}
  ${SectionManageToolbar.fragments.activity}
  ${CancelOneOnOneModal.fragments.refundPolicy}
`;

export const CancelMeetingRequest = gql`
  mutation CancelMeetingRequest($requestUid: ID!) {
    cancelMeetingRequest(requestUid: $requestUid)
  }
`;

export default function useSectionMeetings(
  sectionUid: string,
  startAfter?: Date,
  startBefore?: Date,
) {
  const variables = React.useMemo(
    () => ({
      sectionUid,
      startAfter: startAfter ?? dayjs().startOf("hour").toDate(),
      startBefore:
        startBefore ?? dayjs().startOf("hour").add(90, "days").toDate(),
    }),
    [sectionUid, startAfter, startBefore],
  );

  const { data, loading, error } = useQueryWithPreviousData<
    SectionMeetingsQuery,
    SectionMeetingsQueryVariables
  >(sectionMeetingsQuery, {
    fetchPolicy: "cache-and-network",
    variables,
    skip: !sectionUid,
  });

  const [_cancelMeetingRequest] = useMutation<
    CancelMeetingRequestMutation,
    CancelMeetingRequestMutationVariables
  >(CancelMeetingRequest, {
    optimisticResponse: { cancelMeetingRequest: true },
  });

  const [_requestMeetingReschedule] = useMutation<
    RequestMeetingRescheduleMutation,
    RequestMeetingRescheduleMutationVariables
  >(
    gql`
      mutation RequestMeetingReschedule(
        $meetingUid: ID!
        $proposedTime: StartEndTime!
        $message: String!
        $reschedulesFutureMeetings: Boolean!
      ) {
        requestMeetingReschedule(
          meetingUid: $meetingUid
          proposedTime: $proposedTime
          message: $message
          reschedulesFutureMeetings: $reschedulesFutureMeetings
        ) {
          ...SectionMeetings_RequestFragment
        }
      }
      ${sectionMeetings_RequestFragment}
    `,
  );

  const [_requestMeetings] = useMutation<
    RequestMeetingsMutation,
    RequestMeetingsMutationVariables
  >(
    gql`
      mutation RequestMeetings(
        $sectionUid: ID!
        $proposedTimes: [StartEndTime!]!
        $isRecurring: Boolean
      ) {
        requestMeetings(
          sectionUid: $sectionUid
          proposedTimes: $proposedTimes
          isRecurring: $isRecurring
        ) {
          ...SectionMeetings_RequestFragment
        }
      }
      ${sectionMeetings_RequestFragment}
    `,
  );

  const requestMeetingReschedule = React.useCallback(
    async ({
      meeting,
      newMeetingTime,
      message,
      reschedulesFutureMeetings,
    }: $TSFixMe) => {
      const start_time = newMeetingTime.toDate();
      const end_time = newMeetingTime
        .add(dayjs(meeting.end_time).diff(meeting.start_time))
        .toDate();
      await _requestMeetingReschedule({
        variables: {
          meetingUid: meeting.uid,
          proposedTime: {
            start_time,
            end_time,
          },
          message,
          reschedulesFutureMeetings,
        },
        optimisticResponse: {
          requestMeetingReschedule: {
            __typename: "MeetingRequest",
            uid: v4(),
            start_time,
            end_time,
            status: MeetingRequestStatus.Pending,
            billedOn: startOfIsoWeekUtc(start_time),
            is_recurring: reschedulesFutureMeetings,
          },
        },
        update: (proxy, result) => {
          const meetingRequest = result?.data?.requestMeetingReschedule;
          if (!meetingRequest) {
            return;
          }

          const id = dataIdFromObject(meeting);
          const currentData = proxy.readFragment({
            fragment: sectionMeetings_MeetingFragment,
            fragmentName: "SectionMeetings_MeetingFragment",
            id,
          }) as any;
          if (currentData) {
            proxy.writeFragment({
              fragment: sectionMeetings_MeetingFragment,
              fragmentName: "SectionMeetings_MeetingFragment",
              id,
              data: {
                ...currentData,
                rescheduleRequests: currentData.rescheduleRequests.concat([
                  meetingRequest,
                ]),
              },
            });
          }
        },
      });
    },
    [_requestMeetingReschedule],
  );

  const cancelMeetingRequest = React.useCallback(
    async ({
      request,
      meeting,
    }: {
      request: { uid: string; __typename: string; is_recurring: boolean };
      meeting?: { uid: string; __typename: string };
    }) => {
      await _cancelMeetingRequest({
        variables: {
          requestUid: request.uid,
        },
        // force refetch if cancelling recurring request b/c there's no easy way to bust the cache otherwise
        refetchQueries: request.is_recurring ? ["SectionMeetings"] : undefined,
        update: (proxy, result) => {
          const meetingRequest = result?.data?.cancelMeetingRequest;
          if (!meetingRequest) {
            return;
          }

          if (meeting) {
            const id = dataIdFromObject(meeting);
            const currentData =
              proxy.readFragment<SectionMeetings_MeetingFragmentFragment>({
                fragment: sectionMeetings_MeetingFragment,
                fragmentName: "SectionMeetings_MeetingFragment",
                id,
              });
            if (!currentData) {
              return;
            }
            proxy.writeFragment({
              fragment: sectionMeetings_MeetingFragment,
              fragmentName: "SectionMeetings_MeetingFragment",
              id,
              data: {
                ...currentData,
                rescheduleRequests: currentData.rescheduleRequests.filter(
                  r => r.uid !== request.uid,
                ),
              },
            });
          } else {
            const data = proxy.readQuery<
              SectionMeetingsQuery,
              SectionMeetingsQueryVariables
            >({
              query: sectionMeetingsQuery,
              variables,
            });
            if (!data) {
              return;
            }
            // clone query data but rewrite fields where request uid could exist
            const newData = {
              ...data,
              section: {
                ...data.section,
                // @ts-ignore TS(2531): Object is possibly 'null'.
                meetingRequests: data.section.meetingRequests.filter(
                  r => r.uid !== request.uid,
                ),
              },
            };
            proxy.writeQuery<
              SectionMeetingsQuery,
              SectionMeetingsQueryVariables
            >({
              query: sectionMeetingsQuery,
              variables,
              // @ts-ignore TS(2322): Type '{ section: { meetingRequests: { __typename: ... Remove this comment to see the full error message
              data: newData,
            });
          }
        },
      });
    },
    [_cancelMeetingRequest, variables],
  );

  const requestMeetings = React.useCallback(
    async (proposedTimes: StartEndTime[], isRecurring: boolean = false) => {
      return await _requestMeetings({
        variables: {
          sectionUid,
          proposedTimes,
          isRecurring,
        },
        optimisticResponse: {
          requestMeetings: proposedTimes.map(({ start_time, end_time }) => ({
            __typename: "MeetingRequest",
            uid: v4(),
            start_time,
            end_time,
            status: MeetingRequestStatus.Pending,
            paidAt: null,
            billedOn: startOfIsoWeekUtc(start_time),
            is_recurring: isRecurring,
          })),
        },
        update: (proxy, result) => {
          if (!result?.data?.requestMeetings) {
            return;
          }
          const data = proxy.readQuery<
            SectionMeetingsQuery,
            SectionMeetingsQueryVariables
          >({
            query: sectionMeetingsQuery,
            variables,
          });
          if (!data) {
            return;
          }
          // clone query data but rewrite fields where request uid could exist
          const newData = {
            ...data,
            section: {
              ...data.section,
              // @ts-ignore TS(2531): Object is possibly 'null'.
              meetingRequests: data.section.meetingRequests.concat(
                result?.data?.requestMeetings,
              ),
            },
          };
          proxy.writeQuery<SectionMeetingsQuery, SectionMeetingsQueryVariables>(
            {
              query: sectionMeetingsQuery,
              variables,
              // @ts-ignore TS(2322): Type '{ section: { meetingRequests: { __typename: ... Remove this comment to see the full error message
              data: newData,
            },
          );
        },
      });
    },
    [_requestMeetings, sectionUid, variables],
  );

  const section = data?.section;
  const meetings = section?.meetings;
  const meetingRequests = section?.meetingRequests;
  const enrollments = section?.enrollments;

  return {
    loading,
    error,
    section,
    meetings,
    meetingRequests,
    enrollments,
    requestMeetings,
    requestMeetingReschedule,
    cancelMeetingRequest,
  };
}
